import { useQuery } from '@tanstack/react-query';
import { getMe } from '@/api/auth.api';
import { getAccessToken } from '@/lib/auth/session';
import { ROLES } from '@/lib/constants/roles';
import type { SessionUser } from '@/lib/types/auth.types';

export interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: SessionUser | null;
}

// Set VITE_DEV_SKIP_AUTH=true in .env.local to preview authenticated routes
// without logging in. Guarded by import.meta.env.DEV so VITE_ vars (inlined at
// build time) cannot bypass auth in a production build.
const DEV_MOCK_USER: SessionUser = {
  id: 'dev-user-001',
  email: 'dev@example.com',
  roles: [ROLES.INDIVIDUAL],
  activeMembership: null,
  tier: 'free',
  isWorkEmail: true,
  needsTermsReconsent: false,
};

function isDevSkipAuth(): boolean {
  return (
    import.meta.env.DEV &&
    (import.meta.env as Record<string, string | undefined>)['VITE_DEV_SKIP_AUTH'] === 'true'
  );
}

/**
 * Reflects the backend session. Reads the stored token and hydrates the user
 * via /auth/me (React Query is the single source of server state). Returns a
 * loading flag so route guards can wait rather than flashing the login screen.
 */
export function useAuth(): AuthState {
  const devSkip = isDevSkipAuth();
  const token = getAccessToken();

  const query = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: getMe,
    enabled: !devSkip && Boolean(token),
    retry: false,
    staleTime: Infinity,
    // `/auth/me` now re-resolves permissions LIVE on every call (see the
    // backend AuthController.me() change) - refetching on window-focus regain
    // is what actually surfaces a permission someone else just granted, e.g.
    // an admin toggling a peer's access in another tab, without the affected
    // user needing to relogin. 'always' is required (not just `true`) because
    // staleTime:Infinity means the query is never "stale", so a plain
    // refetchOnWindowFocus would otherwise never fire.
    refetchOnWindowFocus: 'always',
  });

  if (devSkip) {
    return { isAuthenticated: true, isLoading: false, user: DEV_MOCK_USER };
  }
  if (!token) {
    return { isAuthenticated: false, isLoading: false, user: null };
  }
  return {
    isAuthenticated: query.isSuccess,
    isLoading: query.isLoading,
    user: query.data ?? null,
  };
}
